/**
 * ProfileScreen.jsx
 * "Mehr" menu with real customer data
 * i18n enabled
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Wallet, ShoppingCart, Users, User,
  Activity, FileText, Bell, Settings, ChevronRight, LogOut,
  Sparkles
} from 'lucide-react';

export const ProfileScreen = ({ 
  customerFirstName, 
  customerFullName, 
  customerPoints = 0,
  creditBalance = 0,
  onNavigate,
  onLogout
}) => {
  const { t } = useTranslation();

  return (
    <div className="h-full overflow-y-auto p-4 space-y-3 pb-20">
      {/* Profile Header */}
      <div className="flex items-center gap-3 pt-2">
        <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center text-black text-lg font-bold">
          {customerFirstName?.charAt(0)?.toUpperCase() || 'K'}
        </div>
        <div>
          <h1 className="text-white text-base font-bold">{customerFullName}</h1>
          <p className="text-zinc-400 text-xs">Premium</p>
        </div>
      </div>

      {/* Points & Credit Balance Card */}
      <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 rounded-xl p-3 border border-yellow-500/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
            <Wallet className="w-5 h-5 text-yellow-400" />
          </div>
          <div className="flex-1">
            <p className="text-zinc-400 text-[10px]">{t('common.points')}</p>
            <p className="text-white text-lg font-bold">{customerPoints}</p>
          </div>
          <div className="text-right">
            <p className="text-zinc-400 text-[10px]">{t('profile.credit')}</p>
            <p className="text-green-400 text-sm font-medium">CHF {creditBalance?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
        {[
          { icon: User, label: t('profile.title'), color: 'text-yellow-400', screen: 'customer-profile' },
          { icon: Wallet, label: t('profile.account'), color: 'text-yellow-400', screen: 'account' },
          { icon: ShoppingCart, label: t('profile.shop'), color: 'text-blue-400', screen: 'shop' },
          { icon: Users, label: t('profile.community'), color: 'text-purple-400', screen: 'community' },
        ].map(item => (
          <button 
            key={item.screen} 
            onClick={() => onNavigate(item.screen)}
            className="w-full flex items-center gap-4 p-4 border-b border-zinc-700/50 last:border-0 hover:bg-zinc-700/30"
            data-testid={`more-menu-${item.screen}`}
          >
            <item.icon className={`w-4 h-4 ${item.color}`} />
            <span className="text-white text-xs flex-1 text-left">{item.label}</span>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
          </button>
        ))}
      </div>

      {/* Further Options */}
      <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
        {[
          { icon: Activity, label: 'Health Connect', color: 'text-green-400', screen: 'health' },
          { icon: FileText, label: t('profile.documents'), color: 'text-blue-400', screen: 'documents' },
          { icon: Bell, label: t('profile.notifications'), color: 'text-yellow-400', screen: 'notification-settings' },
          { icon: Settings, label: t('profile.settings'), color: 'text-zinc-400', screen: 'settings' },
        ].map(item => (
          <button 
            key={item.screen} 
            onClick={() => onNavigate(item.screen)}
            className="w-full flex items-center gap-4 p-3 border-b border-zinc-700/50 last:border-0 hover:bg-zinc-700/30"
            data-testid={`more-menu-${item.screen}`}
          >
            <item.icon className={`w-4 h-4 ${item.color}`} />
            <span className="text-white text-xs flex-1 text-left">{item.label}</span>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
          </button>
        ))}
      </div>

      {/* Logout Button */}
      <button 
        onClick={() => onLogout && onLogout()}
        className="w-full flex items-center gap-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl"
        data-testid="mobile-logout-button"
      >
        <LogOut className="w-4 h-4 text-red-400" />
        <span className="text-red-400 text-xs flex-1 text-left">{t('profile.logout')}</span>
      </button>
    </div>
  );
};

export default ProfileScreen;
